import React from 'react';

import {assertAndContinue} from 'Asserts';
import {ProcessExecutionType, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {GroupByKey} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

/**
 * A controlled dropdown for selecting a field by which to group search results.
 *
 * A string is a valid option if the search engine defines it as a groupable field. Currently, human-readable
 * labels for each field must be hardcoded in this component, but in the future the server will provide them.
 *
 * A GroupByKey.NONE value means no grouping. This option will be added automatically.
 *
 * The dropdown is disabled if the `options` array is empty, or if it only contains an empty string.
 */
export default function GroupByForm({
  options,
  value,
  onChange,
  disabled,
}: {
  options: Array<GroupByKey>;
  value: GroupByKey;
  onChange: (newKey: string) => void;
  disabled?: boolean;
}): JSX.Element | null {
  if (options.length === 0 || options.every((option) => option === GroupByKey.NONE)) {
    return null;
  }

  return (
    <Dropdown
      menu={
        <Menu value={value} width={MenuWidth.SMALL}>
          {options.map((option) => (
            <Option<string>
              onSelect={onChange}
              value={option}
              key={option}
              dataTestAttrs={{component: 'GroupByFormMenu', element: 'groupByOption', key: option}}
            >
              {getLabel(option)}
            </Option>
          ))}
        </Menu>
      }
    >
      <Button
        size={ButtonSize.SMALL}
        buttonStyle={value ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_GRAY}
        text={value ? `Group by: ${getLabel(value)}` : 'Group by'}
        isDropdown={true}
        disabled={disabled || options.length === 1}
        dataTestAttrs={{component: 'GroupByForm', element: 'GroupByDropdownButton'}}
      />
    </Dropdown>
  );
}

// *** PARTIALLY GENERATED CODE ***
//  This function will be modified by code generation tools. It is okay to add new cases
//  to the switch or modify existing ones, but if the function signature changes, or the
//  body is modified in other ways, the regexes in
//  scripts/search/code_generation/generate_group_by_key.py will need to be updated.
/**
 * Get a human readable label for a group-by field.
 */
function getLabel(groupByField: GroupByKey): React.ReactNode {
  switch (groupByField) {
    case '':
      return 'None';

    case GroupByKey.BLAST_QUERY:
      return 'BLAST Query';

    case GroupByKey.CONTAINER_CONTENTS:
      return 'Contents';

    case GroupByKey.RECIPE_METHOD_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.RECIPES,
        ProcessExecutionType.PROCEDURE_METHOD
      );

    case GroupByKey.RECIPE_RUN_TASK:
      return processExecutionTypeDisplayName(ProcessClientOrigin.RECIPES, ProcessExecutionType.PROCEDURE);

    case GroupByKey.ASSAY_RUN_TASK:
      return processExecutionTypeDisplayName(ProcessClientOrigin.ASSAYS, ProcessExecutionType.PROCEDURE);

    case GroupByKey.ASSAY_METHOD_TASK:
      return processExecutionTypeDisplayName(
        ProcessClientOrigin.ASSAYS,
        ProcessExecutionType.PROCEDURE_METHOD
      );

    case GroupByKey.CONTAINER:
      return 'Container';

    case GroupByKey.SAMPLE:
      return 'Sample';

    default:
      assertAndContinue(false, `Unexpected group by field: ${groupByField}`);
      return groupByField;
  }
}
